// The site-local identity hint, shared between the widget and its host page.
//
// `cc:identity` in localStorage holds who the visitor said they are and which
// studio is theirs — written by the widget when they complete Step 1, and by
// the host site when the My Account portal hands identity back after login
// (the host's receiver stores it under the same key, source 'account').
//
// This is a UX hint, not authentication: it prefes forms and personalises
// chrome. Nothing trusts it server-side — every booking still submits its own
// identity through the normal Step 1 path.
//
// localStorage is per-origin, so the hint personalises the origin that wrote
// it (clubchampion.com), which is the intent — the widget and its host page
// share the origin and therefore the hint.

import type { Location } from '../types';

export interface CcIdentity {
  fullName?: string;
  email?: string;
  /** The visitor's studio, as the booking API knows it. */
  storeId?: string;
  storeName?: string;
  /** Who wrote it: 'booking' (widget Step 1) or 'account' (portal handoff). */
  source: 'booking' | 'account';
  updatedAt: string;
}

const KEY = 'cc:identity';

export function readIdentity(): CcIdentity | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CcIdentity;
    if (typeof parsed !== 'object' || parsed === null) return null;

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Merge-write. An 'account'-sourced identity outranks a 'booking' one — the
 * portal knows who they are; Step 1 only knows what they typed — so a booking
 * write never downgrades the source, but it may still update the store (the
 * customer just told us where they're booking, which is fresher than any
 * profile default).
 */
export function writeIdentity(patch: Partial<CcIdentity> & { source: CcIdentity['source'] }): void {
  try {
    const prev = readIdentity();
    const next: CcIdentity = {
      ...prev,
      ...patch,
      source: prev?.source === 'account' && patch.source === 'booking' ? 'account' : patch.source,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('cc:identity-changed', { detail: next }));
  } catch {
    // Storage unavailable (private mode, quota) — the hint is optional.
  }
}

/**
 * Fold a studio name to a comparable core so the hint's name resolves to the
 * booking API's, even when the two describe the same studio differently. The
 * portal hands back "Chicago Studio" (source 'account'); the booking API's
 * /locations calls it "Chicago" — an exact-string match misses, and the studio
 * silently fails to preselect. Stripping the studio/club-champion boilerplate
 * and all non-alphanumerics leaves "chicago" on both sides.
 */
function normalizeStoreName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b(club\s*champion|studio|golf|fitting|center|centre)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Resolve a stored identity's studio to a Location from the market list.
 *
 * ID is authoritative when present (a 'booking'-sourced hint carries the exact
 * store id); otherwise the name resolves by exact match first, then by the
 * normalized core, then by one name containing the other (so "Chicago" matches
 * a hypothetical "Chicago West" only if nothing better exists). Returns
 * undefined when nothing resolves — a hint that no longer maps to a real studio
 * simply leaves the field empty.
 */
export function matchStoredStore(
  locations: Location[],
  id: Pick<CcIdentity, 'storeId' | 'storeName'>,
): Location | undefined {
  if (id.storeId) {
    const byId = locations.find((l) => l.id === id.storeId);
    if (byId) return byId;
  }
  if (id.storeName) {
    const exact = locations.find((l) => l.name === id.storeName);
    if (exact) return exact;

    const want = normalizeStoreName(id.storeName);
    if (!want) return undefined;

    return (
      locations.find((l) => normalizeStoreName(l.name) === want)
      ?? locations.find((l) => {
        const core = normalizeStoreName(l.name);
        return core !== '' && (core.includes(want) || want.includes(core));
      })
    );
  }
  return undefined;
}
